import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import './style.scss';
import { useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import Input from '../../ui/Input';
import { useAppDispatch } from '../../store';
import { useValidator } from '../../hooks/formValidator/useValidator';
import { isTheSame, hasMinLength, isNotEmpty } from '../../hooks/formValidator/validators';
import Button from '../../ui/Button';
import { APP_ROUTES } from '../../app.config.vars/routes';
import { resetPassword } from '../../api/admin/auth';
import { setIsModalOpen, setModalContent } from '../../store/slices/global';
import { MODAL_CONTENT } from '../../app.config.vars/modalContent';
import { useLocation } from 'react-router-dom';
import { USER_PASSWORD_MIN_LENGTH } from '../../app.config.vars/stored';
import { useFocusOnFirstErrorField } from '../../hooks/errorFieldFocus';
const ResetPasswordForm = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const hash = searchParams.get('hash');
    const [password, setPassword] = useState('');
    const [passwordRepeat, setPasswordRepeat] = useState('');
    const formValidationSchema = {
        password: [isNotEmpty, hasMinLength(USER_PASSWORD_MIN_LENGTH)],
        passwordRepeat: [isTheSame(password)],
    };
    const { formErrors, validateAll, validateInputField } = useValidator({
        inputSchema: formValidationSchema,
    });
    const orderedRefs = [
        ['password', useRef(null)],
        ['passwordRepeat', useRef(null)],
    ];
    const refs = Object.fromEntries(orderedRefs);
    const [submitStatus, setSubmitStatus] = useState(null);
    const [forceFocus, setForceFocus] = useState(false);
    useFocusOnFirstErrorField(formErrors, orderedRefs, forceFocus, setForceFocus);
    const handleSubmit = async () => {
        const errors = validateAll({
            inputValues: { password, passwordRepeat },
            context: {
                originalValue: password,
            },
        });
        if (Object.keys(errors).length > 0) {
            setForceFocus(true);
            return;
        }
        setSubmitStatus('pending');
        try {
            await resetPassword({ password, hash: hash });
            setSubmitStatus('success');
        }
        catch (err) {
            if (err.response.data.error.message === 'Invalid hash.' ||
                err.response.data.error.message === 'Hash expired.') {
                setSubmitStatus('error');
            }
            else {
                dispatch(setIsModalOpen(true));
                dispatch(setModalContent(MODAL_CONTENT.somethingWentWrong.key));
            }
        }
    };
    return (_jsxs("form", { className: 'reset-password-form', onSubmit: (event) => event.preventDefault(), children: [_jsx(Input, { ref: refs.password, id: 'password', name: 'password', value: password, type: "password", placeholder: t('placeholder.password'), onChange: (event) => {
                    setPassword(event.currentTarget.value);
                    validateInputField({ fieldName: 'password', value: event.currentTarget.value });
                }, error: !!formErrors.password?.length, errorMessage: !!formErrors.password?.length
                    ? t(formErrors.password[0].messageTranslationKey, formErrors.password[0].variableToUseInMessage)
                    : undefined }), _jsx(Input, { ref: refs.passwordRepeat, id: 'password-repeat', name: 'passwordRepeat', value: passwordRepeat, type: "password", placeholder: t('placeholder.password_confirm'), onChange: (event) => setPasswordRepeat(event.currentTarget.value), error: !!formErrors.passwordRepeat?.length, errorMessage: !!formErrors.passwordRepeat?.length
                    ? t(formErrors.passwordRepeat[0].messageTranslationKey, formErrors.passwordRepeat[0].variableToUseInMessage)
                    : undefined }), submitStatus === 'success' && (_jsx("div", { className: "reset-password-instruction-message", children: _jsx(Trans, { i18nKey: "admin.password_changed_instruction", components: [_jsx("a", { href: APP_ROUTES.adminLogin })] }) })), submitStatus === 'error' && (_jsx("div", { className: "reset-password-instruction-message", children: _jsx(Trans, { i18nKey: "admin.recovery_link_expired_instruction", components: [_jsx("a", { href: APP_ROUTES.adminForgotPassword })] }) })), _jsx("div", { className: "reset-password-form-submit", children: _jsx(Button, { onClick: handleSubmit, label: t('button.submit_new_password'), disabled: submitStatus !== null }) })] }));
};
export default ResetPasswordForm;
